import { ProjectRole } from '@internal/components/projects/helper/project-role';
import {
  RoleStep,
  RoleStepAction,
  StepStatusTone,
} from '@internal/components/roles-hub/roles-hub.types';
import { ApplicationState, Project } from '@internal/types/projects';

export { StepStatusTone };

export enum ScheduledCallKind {
  Technical = 'technical',
  Screening = 'screening',
}

export interface RoleProgress extends ProjectRole {
  started: boolean;
  verified: boolean;
  seniority: string | null;
  stopped: boolean;
  capacityWaitlisted: boolean;
  outcomeLabel: string | null;
  scheduledAt: string | null;
  scheduledKind: ScheduledCallKind | null;
  needsBooking: boolean;
  technicalDone: boolean;
  waiting: boolean;
}

export enum ApplicationTarget {
  Projects = 'projects',
  Profile = 'profile',
  Roles = 'roles',
  RolesExplore = 'roles-explore',
  Community = 'community',
}

export interface ApplicationLinkAction {
  label: string;
  target: ApplicationTarget;
  primary?: boolean;
}

export type ApplicationAction = ApplicationLinkAction | RoleStepAction;

export const isLinkAction = (
  action: ApplicationAction
): action is ApplicationLinkAction => 'target' in action;

export const isActionDisabled = (action: ApplicationAction): boolean =>
  !isLinkAction(action) && Boolean(action.disabled);

export const exploreProjects = (label: string): ApplicationLinkAction => ({
  label,
  target: ApplicationTarget.Projects,
});

export interface BannerModel {
  title: string;
  detail: string;
  next: ApplicationLinkAction | null;
  allowFallback: boolean;
}

export interface StepStatus {
  label: string;
  tone: StepStatusTone;
}

export enum RoleStageStatus {
  Completed = 'completed',
  Current = 'current',
  Upcoming = 'upcoming',
}

export interface RoleStageModel {
  label: string;
  status: RoleStageStatus;
}

export interface RoleDetailsModel {
  stages: RoleStageModel[] | null;
  title: string;
  text: string;
  status?: StepStatus;
  action?: ApplicationAction;
}

export interface StepModel {
  key: string;
  title: string;
  text: string;
  done: boolean;
  active: boolean;
  status?: StepStatus;
  action?: ApplicationAction;
  details?: RoleDetailsModel;
}

export interface SideRailModel {
  title: string;
  text: string;
  action: ApplicationLinkAction;
}

export interface OutcomeFooterModel {
  savedNote: boolean;
  explore: ApplicationLinkAction | null;
}

export interface ApplicationViewModel {
  banner: BannerModel;
  statusLabel: string;
  steps: StepModel[];
  outcomeTitle: string;
  outcomeEnded: boolean;
  outcome: StepModel[];
  outcomeFooter: OutcomeFooterModel;
  showWithdraw: boolean;
  sideRail: SideRailModel | null;
}

export interface BuildInput {
  state: ApplicationState;
  project: Project;
  profileReady: boolean;
  paused: boolean;
  onboarded: boolean;
  hideRoleActions: boolean;
  roleProgress: RoleProgress;
  roleSteps: RoleStep[];
}
